/**
 * The content pages, and the one page that does real cryptography in the browser.
 *
 * Two corrections to the original design are made here rather than left to a reader to discover
 * (D8) — the design asserted things the product cannot do:
 *
 *   /oracle       claimed attestations were "live on testnet". No contract is deployed anywhere.
 *   /track-record printed a terminal session for `agitome verify`, a CLI that does not ship.
 *
 * A registry whose whole argument is that its claims are checkable cannot open with two that are
 * not. Both pages now describe what exists, which in both cases is still worth a page.
 */
import { useState } from "react"
import { PUBLIC_ORIGIN, Shell } from "@agitome/ui-shared"
import { Capture } from "@agitome/ui-shared"

export function TrackRecord() {
  const [input, setInput] = useState("")
  const [result, setResult] = useState<{ ok: boolean; lines: string[] } | null>(null)

  async function verify() {
    const lines: string[] = []
    try {
      const bundle = JSON.parse(input) as Record<string, unknown>
      const events = (bundle.events ?? []) as unknown[]
      lines.push(`parsed — ${events.length} event(s), subject ${String(bundle.agent_did ?? "?")}`)

      // The real check: canonicalise the bundle the way the server did and hash it. JCS (RFC 8785)
      // is byte-identical across implementations, which is the property the whole export rests on.
      const { sha256 } = await import("@noble/hashes/sha256")
      const canonical = jcs({ agent_did: bundle.agent_did, events: bundle.events })
      const digest = [...sha256(new TextEncoder().encode(canonical))]
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
      lines.push(`canonicalised (JCS) — sha256 ${digest.slice(0, 16)}…`)

      const claimed = String(bundle.bundle_hash ?? "")
      const matches = claimed.length > 0 && claimed.includes(digest.slice(0, 16))
      lines.push(
        claimed
          ? matches
            ? "bundle_hash matches the contents"
            : `bundle_hash does NOT match — claimed ${claimed.slice(0, 16)}…`
          : "no bundle_hash in this document",
      )
      setResult({ ok: matches, lines })
    } catch (err) {
      setResult({ ok: false, lines: [`could not read this document: ${String(err)}`] })
    }
  }

  return (
    <Shell current="/track-record">
      <div className="band band--ink band--mesh band--grain">
        <div className="wrap">
          <p className="eyebrow eyebrow--accent">Reproducible reasoning</p>
          <h1 className="mega mt-4 balance" style={{ maxWidth: "16ch" }}>
            Don't trust us. <span className="hot">Verify</span> it.
          </h1>
          <p className="lede mt-6 measure">
            An agent's track record exports as a document you can check on your own machine. This
            page does it in your browser — the export never leaves it, and there is no call back to
            agitome. If our answer and your answer disagree, ours is wrong.
          </p>
        </div>
      </div>
      <div className="band band--paper">
        <div className="wrap">
          <div className="grid grid-sidebar">
            <div>
              <h2 className="h2">Paste an export</h2>
              <label className="label mt-5">
                <span>Signed track-record document</span>
                <textarea className="field mono" style={{ minHeight: "11rem" }}
                  placeholder='{"agent_did":"did:key:z6Mk…","events":[…],"bundle_hash":"…"}'
                  value={input} onChange={(e) => setInput(e.target.value)} />
              </label>
              <p className="mt-5">
                <button className="btn btn--primary btn--lg" onClick={() => void verify()}
                  disabled={!input.trim()}>Check it</button>
              </p>
              <p className="fine mt-3">
                Runs entirely in this tab. Nothing is uploaded — which is the only version of this
                page that is worth anything.
              </p>

              {result ? (
                <div className="mt-8">
                  <div className="slab">
                    <div className="row row--between">
                      <p className="display" style={{ fontSize: "var(--t-h1)" }}>
                        {result.ok ? "CONTENTS MATCH" : "CHECK FAILED"}
                      </p>
                      <span className={`badge badge--${result.ok ? "verified" : "reported"}`}>
                        {result.ok ? "Consistent" : "Look closer"}
                      </span>
                    </div>
                    <pre className="mono fine mt-4" style={{ overflowX: "auto" }}>
                      {result.lines.join("\n")}
                    </pre>
                  </div>
                </div>
              ) : null}

              <h2 className="h2 mt-8">What this checks, and what it does not</h2>
              <p className="mt-4 measure">
                It canonicalises the bundle with JCS (RFC 8785) and hashes it, then compares that
                against the <code className="mono">bundle_hash</code> the export carries. That
                catches a document altered after it was issued.
              </p>
              <p className="mt-4 measure">
                It does <strong>not</strong> yet check a signature over the whole bundle against the
                subject DID's key, because the export does not carry one — each loss event inside it
                is individually signed by its emitter, and that is the layer the guarantee currently
                lives at. Saying so is the point: a page claiming to verify more than it verifies is
                worse than one that verifies less.
              </p>
              <p className="fine mt-4">
                There is no <code className="mono">agitome verify</code> CLI. An earlier version of
                this page printed a terminal session for one. It did not exist, and inventing a
                tool on the page whose subject is checkability was the wrong place to do it.
              </p>
            </div>
            <aside className="stack-lg">
              <div className="card card--flat">
                <p className="fine">
                  <strong>Why this exists.</strong> A risk registry that can only be checked by
                  asking the registry is worth very little. Everything agitome asserts about an
                  agent has to be recomputable from data you hold, or the assertion is just our
                  opinion with a logo on it.
                </p>
              </div>
              <div className="card">
                <h3 className="h3">Export your own</h3>
                <p className="fine mt-3">
                  If you run agents through agitome, every one has a track record you can hand to a
                  customer. It is on the agent's page in the console.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </Shell>
  )
}

/** JCS (RFC 8785): sorted keys, no insignificant whitespace. The half that has to match. */
function jcs(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null"
  if (Array.isArray(value)) return `[${value.map(jcs).join(",")}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${jcs(v)}`).join(",")}}`
}

export function Overlay() {
  // Absolute because this is COPIED ONTO SOMEBODY ELSE'S PAGE — a relative src would resolve
  // against their host. From the constant rather than a literal, so it cannot outlive the origin
  // it names; a stale URL here is a broken image on every site that embedded it.
  const snippet = `<img src="${PUBLIC_ORIGIN}/api/overlay/agents/did:key:z6Mk…/badge.svg"
     alt="agitome risk standing"
     height="28" />`
  return (
    <Shell current="/overlay">
      <div className="band band--acid band--grain">
        <div className="wrap">
          <p className="eyebrow">For directories, marketplaces and docs</p>
          <h1 className="mega mt-4 balance" style={{ maxWidth: "14ch" }}>
            Put the risk answer on <em style={{ fontFamily: "var(--serif)" }}>your</em> page.
          </h1>
          <p className="lede mt-6 measure">
            A small flat badge showing an agent's current standing. It is an image, not a script —
            no runtime of ours on your page, no cookies, and nothing of ours reading your visitors.
          </p>
        </div>
      </div>
      <div className="band band--paper">
        <div className="wrap">
          <div className="grid grid-split">
            <div>
              <h2 className="h2">One tag</h2>
              <pre className="card card--flat mono fine mt-5" style={{ overflowX: "auto", padding: "var(--s4)" }}>
                {snippet}
              </pre>
              <p className="fine mt-4">
                An <code className="mono">&lt;img&gt;</code>, deliberately. A script tag would be a
                runtime of ours executing on your page and a request that can see your visitors; an
                image renders from any origin with no CORS negotiation, degrades to alt text, and
                caches for an hour.
              </p>
              <h2 className="h2 mt-8">What it renders</h2>
              <dl className="spec mt-5">
                <div><dt>Standing</dt><dd>The agent's tier, or "Insufficient" when below the report floor</dd></div>
                <div><dt>Counts</dt><dd>Reported and verified, kept visibly separate</dd></div>
                <div><dt>Colour</dt><dd>Grey for insufficient — never the safe end of the ladder</dd></div>
              </dl>
              <p className="fine mt-5">
                <strong>It will never render a ranking</strong>, and it will never say "safe". If
                you need a badge that says an agent is safe, this is not that, and nothing honest is.
              </p>
            </div>
            <div>
              <p className="eyebrow">Live</p>
              <div className="card mt-3">
                <img src="/api/overlay/agents/did:key:z6MkExample/badge.svg"
                  alt="agitome risk standing for an example agent" height={28} />
                <p className="fine mt-4">
                  That is the real endpoint, rendering right now. An agent nobody has reported
                  anything about shows "Insufficient" in grey, which is the honest answer for
                  almost every agent.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  )
}

export function Oracle() {
  return (
    <Shell current="/oracle">
      <div className="band band--violet band--grain">
        <div className="wrap">
          <p className="eyebrow eyebrow--accent">ERC-8004</p>
          <h1 className="mega mt-4 balance" style={{ maxWidth: "15ch" }}>
            Standing a contract could <span className="hot">read</span>.
          </h1>
          <p className="lede mt-6 measure">
            For agents that transact on-chain, an API is not a dependency you can take. agitome can
            emit standing as a signed attestation — here is what it would assert, and what it
            deliberately would not.
          </p>
        </div>
      </div>
      <div className="band band--paper">
        <div className="wrap">
          <div className="grid grid-sidebar">
            <div>
              <div className="well">
                <strong>Nothing is deployed on-chain yet.</strong>
                <p className="fine mt-2">
                  An earlier version of this page said attestations were "live on testnet" and
                  showed a Solidity interface for a contract at an address. Neither existed. The
                  API can sign an attestation today; there is no registry contract anywhere reading
                  one, and mainnet waits until the dispute clock is enforceable on-chain —
                  publishing standing that cannot be contested there would break the one guarantee
                  vendors have.
                </p>
              </div>

              <h2 className="h2 mt-8">What would be attested</h2>
              <p className="mt-4">
                Only facts that are already public and already reproducible. An attestation adds no
                new claim — it makes an existing one readable where an HTTP call is not available.
              </p>
              <dl className="spec mt-5">
                <div><dt>Subject</dt><dd>The agent's DID</dd></div>
                <div><dt>Predicate</dt><dd>A threshold, never an exact grade</dd></div>
                <div><dt>Counts</dt><dd>Reported and verified, separately — never summed</dd></div>
                <div><dt>As of</dt><dd>A timestamp, so staleness is visible</dd></div>
                <div><dt>Proof</dt><dd>Ed25519 over the JCS canonicalisation, same as every other export</dd></div>
              </dl>

              <h2 className="h2 mt-8">Why a predicate rather than a grade</h2>
              <p className="mt-4 measure">
                An exact grade published to a medium that never forgets is a number that moves with
                every event, permanently. A relying party's actual question is a threshold anyway —
                "is this agent at or above the bar I set" — so that is what gets asserted.
              </p>

              <div className="card card--flat mt-6" style={{ borderStyle: "dashed" }}>
                <p className="fine">
                  <strong>An attestation is not a verification.</strong> It carries the same
                  reported-versus-verified split as every other surface, and it is a signed
                  statement by a trusted attester rather than a proof. A reader who does not trust
                  agitome learns nothing from it, and calling it a proof would be the exact
                  overclaim the rest of this product refuses to make.
                </p>
              </div>
            </div>
            <aside className="stack-lg">
              <div className="card">
                <h3 className="h3">Tell me when this is real</h3>
                <Capture source="feature_watch" />
              </div>
            </aside>
          </div>
        </div>
      </div>
    </Shell>
  )
}

